import React, { useEffect, useState } from 'react';
import { useSession } from '@/hooks/useSession';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FeedCommentAnswers } from '@/pages/feed/feed-comment-answers';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { PostActionsButton, PostActionsItem, PostActionsWrapper, PostComment, PostCommentBody, PostCommentContent, PostCommentHeader, PostCommentName, PostCommentText, PostCommentTime } from '@/components/custom/post';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { FiTrash2 } from 'react-icons/fi';
import { Loader } from '@/components/custom/loader';
import ChatFileCard from '@/components/custom/chat/chat-file-card';

export function CourseClassroomCommentsCard(props) {
  const { comment, deleteCommentFromParent, comments, learningSystemItemId, sendAllMessages, setCommentsMeta } = props;
  const { session } = useSession();
  const client = useClient();
  const authorityLevel = useAuthorityLevel(session);
  const [isOpenAnswer, setIsOpenAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const findAnswers = () => {
    const commentsWithAnswers = comments?.filter(comment => comment?.['comment-parent-id'] !== null);
    const answersByCommentId = commentsWithAnswers?.filter(item => item?.['comment-parent-id'] === comment?.id).reverse();

    setAnswers(answersByCommentId);
  };

  const handleCreateAnswer = async value => {
    try {
      const response = await client.mutate('/comments', {
        'comment-parent-id': comment.id,
        body: value,
        'learning-system-item-id': learningSystemItemId
      });

      setAnswers(prev => [response.data, ...prev]);
      setCommentsMeta(prev => ({
        ...prev,
        'daily-comments-count': prev['daily-comments-count'] + 1
      }));
      toast.success('Resposta criada com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao responder');
    }
  };

  const handleDeleteAnswer = async answerId => {
    setIsDeleting(true);

    try {
      const { error } = await client.delete(`/comments/${answerId}`);

      if (error) {
        console.error(error);
        toast.error('Erro ao deletar resposta');
        return;
      }

      setAnswers(prev => prev.filter(item => item.id !== answerId));
    } catch (error) {
      console.error('Erro de rede/execução:', error);
      toast.error('Erro ao deletar resposta');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    findAnswers();
  }, [comments, comment]);

  const handleMainCommentDelete = () => {
    if (deleteCommentFromParent) {
      deleteCommentFromParent(comment, setIsDeleting);
    }
  };

  const handleActionClick = () => {
    const fileType = comment?.file?.['content-type'];
    const fileUrl = comment?.file?.url;

    if (fileType && fileUrl) {
      if (fileType.startsWith('video/')) {
        window.open(fileUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = comment?.file?.name || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const renderTextWithLinks = text => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return text?.split(urlRegex).map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-primary-dark underline"
          >
            {part}
          </a>
        );
      }

      return part;
    });
  };

  return (
    <PostComment>
      {isDeleting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
          <Loader />
        </div>
      )}

      <Link
        to={`/usuario/metricas/${comment?.user?.id}`}
        state={{ isRedirected: true }}
      >
        <AvatarWrapper
          size="sm"
          src={comment?.user?.image?.url}
          text={comment?.user?.name}
        />
      </Link>

      <PostCommentContent>
        <PostCommentHeader>
          <PostCommentName>
            <Link
              to={`/usuario/metricas/${comment?.user?.id}`}
              state={{ isRedirected: true }}
            >
              {comment?.user?.name || 'Usuário do sistema'}
            </Link>
          </PostCommentName>
          <PostCommentTime>{moment(comment['created-at']).fromNow()}</PostCommentTime>
        </PostCommentHeader>

        <PostCommentBody>
          <PostCommentText>{renderTextWithLinks(comment.body)}</PostCommentText>
          {comment?.attachments?.length > 0 &&
            comment.attachments.map(attachment => (
              <ChatFileCard
                key={attachment.id}
                file={attachment}
                handleActionClick={handleActionClick}
              />
            ))}
        </PostCommentBody>

        <FeedCommentAnswers
          answers={answers}
          isOpenAnswer={isOpenAnswer}
          onSubmit={handleCreateAnswer}
          onDelete={handleDeleteAnswer}
        />
      </PostCommentContent>

      <div className="flex flex-col gap-2 pt-8">
        <Button
          variant="link"
          size="xs"
          style={{ opacity: isOpenAnswer || sendAllMessages ? 0.5 : 1 }}
          onClick={() => setIsOpenAnswer(prev => !prev)}
          disabled={sendAllMessages}
        >
          Responder
        </Button>

        {(authorityLevel === 'admin' || comment?.user?.id === session?.user?.id) && (
          <PostActionsWrapper className="w-full flex-row">
            <PostActionsItem className="flex grow flex-row justify-end">
              <PostActionsButton
                isDanger
                onClick={handleMainCommentDelete}
                className="mr-0"
              >
                <FiTrash2 size={16} />
              </PostActionsButton>
            </PostActionsItem>
          </PostActionsWrapper>
        )}
      </div>
    </PostComment>
  );
}
